import React, { useEffect, useState } from 'react';
import { Grid, Link } from '@material-ui/core';
import { LoadingBounce, Panel, useDialogs } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import * as Icons from '@material-ui/icons';
import { getOptionLabel } from '@michelin/select-options-provider';
import uuid from 'uuid';
import { cloneDeep } from 'lodash';
import { sleep } from 'Utils';
import {
  Contact, ContactListItem, SPPContactTypeLevelsEnum, SPPContactTypeServicesEnum,
} from '../../../Types';
import { PlainMessage } from '../../PlainMessage';
import { updateContactTypes, useContactList } from '../../../hooks/useContactQueries';
import { getCustomActions, loadContact, remove } from './Utils';
import { getNewContact, RetrieveRoleAreaPriorityLevel } from '../Utils';
import ContactsListModal from '../ListModal';
import { ContactDetailModal } from '../DetailModal';
import { getContactTypeMapKey } from '../ContactTypes/utils';

interface AssignContactProps {
  shipToNumber: string,
  billToNumber: string,
  countryCode: string,
  title?: string,
  service: string,
  roleArea: string,
  priorityLevels: SPPContactTypeLevelsEnum[],
  mode: string,
  onChange?: (modified: boolean, primaryAssigned: boolean, save: (active: boolean) => void) => void,
}

function AssignContact(props: AssignContactProps) {
  const {
    shipToNumber, billToNumber, countryCode, title, service, roleArea, priorityLevels, mode,
  } = props;
  const { t } = useTranslation();
  const { errorDialog } = useDialogs();

  const panelTitle = (title)
        || `${getOptionLabel('dealer_dealer_contact_types_services', service)} ${getOptionLabel('dealer_dealer_contact_types_role_areas', roleArea)}`;

  const [primaryContact, setPrimaryContact] = useState<ContactListItem | undefined>(undefined);
  const [secondaryContact, setSecondaryContact] = useState<ContactListItem | undefined>(undefined);

  const [allowRemovePrimary, setAllowRemovePrimary] = useState<boolean>(false);
  const [allowRemoveSecondary, setAllowRemoveSecondary] = useState<boolean>(false);

  const [saving, setSaving] = useState<boolean>(false);
  const [forceReload, setForceReload] = useState<number>(Date.now());
  const [creating, setCreating] = useState<{ priorityLevel: string, assignLevel: 'ST' } | null>(null);
  const [assigning, setAssigning] = useState<{ priorityLevel: string, assignLevel: 'ST' | 'BT' | 'All' } | null>(null);

  const contactListQuery = useContactList({
    stNumber: shipToNumber,
    accountLevel: '',
    contactType: roleArea,
    queryKey: `assign-contact-${forceReload.toString()}`,
  });

  const assignedContactsMap = new Map<string, ContactListItem>();

  useEffect(() => {
    if (contactListQuery.isSuccess && contactListQuery.isFetched && contactListQuery.data?.data?.length) {
      const tempMap = new Map<string, ContactListItem>();
      contactListQuery.data.data.forEach((contact: ContactListItem) => {
        const priorityLevel: string | null = RetrieveRoleAreaPriorityLevel(contact, SPPContactTypeServicesEnum.retail, roleArea);
        if (priorityLevel && priorityLevel !== SPPContactTypeLevelsEnum.general) {
          tempMap.set(getContactTypeMapKey(service, roleArea, priorityLevel, contact.contact_level.toLowerCase()), contact);
        }
      });

      if (priorityLevels.includes(SPPContactTypeLevelsEnum.primary)) {
        loadContact(tempMap, assignedContactsMap, service, roleArea, SPPContactTypeLevelsEnum.primary);
      }

      if (priorityLevels.includes(SPPContactTypeLevelsEnum.secondary)) {
        loadContact(tempMap, assignedContactsMap, service, roleArea, SPPContactTypeLevelsEnum.secondary);
      }

      const primary = assignedContactsMap.get(getContactTypeMapKey(service, roleArea, SPPContactTypeLevelsEnum.primary));
      const secondary = assignedContactsMap.get(getContactTypeMapKey(service, roleArea, SPPContactTypeLevelsEnum.secondary));

      setPrimaryContact(primary);
      setSecondaryContact(secondary);

      // Primary cannot be removed if it is BT.
      // setAllowRemovePrimary(primary?.contact_level === 'ST');
      // Primary cannot be removed at all
      setAllowRemovePrimary(false);
      // Secondary cannot be removed if it is BT.
      setAllowRemoveSecondary(secondary?.contact_level === 'ST');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactListQuery.data]);

  useEffect(() => {
    async function reload() {
      await sleep(2000);
      contactListQuery.refetch();
    }

    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceReload]);

  if (!contactListQuery.isSuccess) {
    return (
      <Panel title={panelTitle}>
        <Grid container spacing={2} style={{ paddingLeft: 7 }}>
          <Grid item xs={12}>
            <LoadingBounce />
          </Grid>
        </Grid>
      </Panel>
    );
  }

  if (contactListQuery.isError) {
    return (
      <Panel title={panelTitle}>
        <Grid container spacing={2} style={{ paddingLeft: 7 }}>
          <Grid item xs={12}>
            <PlainMessage
              title={t('Database Error')}
              messages={t('Application could not load location data.\nPlease try again later or contact support if the error persists').split('\n')}
            />
          </Grid>
        </Grid>
      </Panel>
    );
  }

  /// ////////////////////////
  /// Set Contact Functions //
  /// ////////////////////////

  function setContact(contact: Contact | ContactListItem, priorityLevel: string = 'general') {
    const c = cloneDeep<ContactListItem>(contact as unknown as ContactListItem);
    switch (priorityLevel) {
      case SPPContactTypeLevelsEnum.primary:
        setPrimaryContact(c);
        break;
      case SPPContactTypeLevelsEnum.secondary:
        setSecondaryContact(c);
        break;
      default:
        break;
    }
  }

  /// ///////////////////
  /// Render Functions //
  /// ///////////////////

  function renderContactInformationBlock(data: string | null, Icon: any): JSX.Element {
    if (!data || !data.length || !data.trim()) return <></>;
    const icnProps = { style: { height: 14 } };
    return (
      <div style={{ whiteSpace: 'nowrap' }}>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <Icon {...icnProps} />
        {' '}
        {data}
      </div>
    );
  }

  function renderAssignRemoveActions(contact: ContactListItem, priorityLevel: SPPContactTypeLevelsEnum, allowAdd: boolean, allowRemove: boolean): JSX.Element {
    if (mode !== 'edit' || saving) return <></>;

    let removeContent = null;
    let assignLevel: 'ST' | 'BT' | 'All' = 'ST';
    const currentContactPriorityLevel: string | null = RetrieveRoleAreaPriorityLevel(contact, SPPContactTypeServicesEnum.retail, roleArea);
    const temporary: boolean = (currentContactPriorityLevel !== priorityLevel);
    if (allowRemove && ((temporary && contact.contact_level === 'BT') || contact.contact_level === 'ST')) {
      if (contact.contact_level === 'BT') assignLevel = 'All';
      removeContent = (
        <Link
          style={{ cursor: 'pointer' }}
          onClick={async () => {
            setSaving(true);
            const response = await remove(contact, SPPContactTypeServicesEnum.retail, roleArea, priorityLevel, shipToNumber);
            if (!response.status) {
              errorDialog(
                response.messages.map((x: any) => t(x.message, x.variables)),
                response.message,
              );
            } else {
              setForceReload(Date.now());
            }
            setSaving(false);
          }}
        >
          [
          {t('Remove')}
          ]
        </Link>
      );
    }

    if (allowAdd) {
      return (
        <>
          {removeContent}
                    &nbsp;
          <Link
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setAssigning({ priorityLevel, assignLevel });
            }}
          >
            [
            {t('Assign')}
            ]
          </Link>
                    &nbsp;
          <Link style={{ cursor: 'pointer' }} onClick={() => setCreating({ priorityLevel, assignLevel: 'ST' })}>
            [
            {t('Create')}
            ]
          </Link>
        </>
      );
    }

    return <></>;
  }

  function renderAssignCreateActions(
    priorityLevel: SPPContactTypeLevelsEnum,
    allowAdd: boolean,
  ): JSX.Element {
    if (mode !== 'edit' || !allowAdd || saving) return <></>;

    return (
      <>
        <Link
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setAssigning({ priorityLevel, assignLevel: 'All' });
          }}
        >
          [
          {t('Assign')}
          ]
        </Link>
                &nbsp;
        <Link style={{ cursor: 'pointer' }} onClick={() => setCreating({ priorityLevel, assignLevel: 'ST' })}>
          [
          {t('Create')}
          ]
        </Link>
      </>
    );
  }

  function renderContactAssigned(
    contact: ContactListItem | undefined,
    priorityLevel: SPPContactTypeLevelsEnum,
    allowAdd: boolean,
    allowRemove: boolean,
  ): JSX.Element {
    return (
      <>
        <Grid container style={{ marginTop: 15, marginBottom: 15 }}>
          <Grid item xs={6} md={12} lg={6} style={{ textAlign: 'left' }}>
            <h3 style={{ marginBottom: 10, marginTop: 10 }}>
              {getOptionLabel('dealer_dealer_contact_types_levels', priorityLevel)}
              {' '}
              {panelTitle}
              {' '}
              {priorityLevel === SPPContactTypeLevelsEnum.primary ? '*' : ''}
            </h3>
          </Grid>
          <Grid
            item
            xs={6}
            md={12}
            lg={6}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
          >
            {(contact)
              ? renderAssignRemoveActions(contact, priorityLevel, allowAdd, allowRemove)
              : renderAssignCreateActions(priorityLevel, allowAdd)}
          </Grid>
          <Grid item xs={6} style={{ textAlign: 'left' }}>
            {contact && renderContactInformationBlock(`${contact.first_name} ${contact.last_name} [ ${contact.contact_level} ]`, Icons.Person)}
            {contact && renderContactInformationBlock(contact.work_phone, Icons.Phone)}
            {contact && renderContactInformationBlock(contact.email_address, Icons.Email)}
            {!contact && (
            <span style={{ color: '#999', fontStyle: 'italic' }}>
              (
              {t('not assigned')}
              )
            </span>
            )}
          </Grid>
        </Grid>
      </>
    );
  }

  /// ///////////////////
  /// Render Modal Fn ///
  /// ///////////////////

  function RenderCreating() {
    const modalTitle = t(
      'Create {{level}} {{roleAreaName}} Contact',
      {
        level: getOptionLabel('dealer_dealer_contact_types_levels', creating?.priorityLevel || 'general'),
        roleAreaName: getOptionLabel('dealer_dealer_contact_types_role_areas', roleArea),
      },
    );

    const contactTypes = [{
      service,
      role_areas: [
        {
          role_area: roleArea,
          levels: [
            {
              level: 'general',
              location: `10~${shipToNumber}`,
            },
          ],
        },
      ],
      id: uuid.v4(),
    }];

    const data: Contact = getNewContact({ contact_types: contactTypes });

    return (
      <ContactDetailModal
        title={modalTitle}
        defaultData={data}
        onClose={() => {
          setCreating(null);
        }}
        onSave={async (contact: Contact) => {
          const data: any = {
            data: [
              {
                contactId: contact.id,
                service,
                roleArea,
                level: creating?.priorityLevel || 'general',
                action: 'add',
              },
            ],
          };

          await updateContactTypes(shipToNumber, data);
          setContact(contact, creating?.priorityLevel);
          setForceReload(Date.now());
          setCreating(null);
        }}
        countryCode={countryCode}
      />
    );
  }

  function RenderAssigning() {
    const actionsParams: any = {
      service: SPPContactTypeServicesEnum.retail,
      roleArea,
      level: assigning?.priorityLevel || '',
      location: shipToNumber,
      callback: (contact: ContactListItem) => {
        setContact(contact, creating?.priorityLevel);
        setForceReload(Date.now());
        setAssigning(null);
      },
    };

    const modalTitle = t(
      'Assign {{level}} {{roleAreaName}} Contact',
      {
        level: getOptionLabel('dealer_dealer_contact_types_levels', assigning?.priorityLevel || 'general'),
        roleAreaName: getOptionLabel('dealer_dealer_contact_types_role_areas', roleArea),
      },
    );

    return (
      <ContactsListModal
        onClose={() => {
          setAssigning(null);
        }}
        title={modalTitle}
        stNumber={shipToNumber}
        btNumber={billToNumber}
        contactLevel={assigning?.assignLevel || 'All'}
        customActions={getCustomActions(actionsParams, t)}
      />
    );
  }

  return (
    <>
      {(creating) ? RenderCreating() : null}
      {(assigning) ? RenderAssigning() : null}
      <Panel title={panelTitle}>
        <Grid container spacing={2} style={{ paddingLeft: 7 }}>
          <Grid item xs={12}>
            {priorityLevels.includes(SPPContactTypeLevelsEnum.primary)
                        && renderContactAssigned(primaryContact, SPPContactTypeLevelsEnum.primary, true, allowRemovePrimary)}

            {priorityLevels.includes(SPPContactTypeLevelsEnum.primary)
                        && priorityLevels.includes(SPPContactTypeLevelsEnum.secondary) ? <hr /> : <></>}

            {priorityLevels.includes(SPPContactTypeLevelsEnum.secondary)
                        && renderContactAssigned(secondaryContact, SPPContactTypeLevelsEnum.secondary, !!primaryContact, allowRemoveSecondary)}
          </Grid>
        </Grid>
      </Panel>
    </>
  );
}

export {
  AssignContact,
};
